import React, { useEffect, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Download, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

/**
 * Compact PDF viewer with page navigation + zoom.
 * `src` must be a full URL served by the backend `/api/files/…` — we fetch with the auth
 * token and hand a Blob URL to react-pdf so private files stay private.
 */
export default function PdfViewer({ src, className = "" }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let created = null;
    (async () => {
      if (!src) return;
      try {
        setErr(null);
        // `src` is already a full backend path like "/api/files/..." — don't re-prefix with
        // the `/api` base (that produced the reported "/api/api/files/..." 404).
        const fullUrl = src.startsWith("http") ? src : `${process.env.REACT_APP_BACKEND_URL}${src}`;
        const token = localStorage.getItem("wrm_token");
        const res = await fetch(fullUrl, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (!res.ok) throw new Error(`${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        created = URL.createObjectURL(blob);
        setBlobUrl(created);
      } catch (e) {
        if (!cancelled) setErr(String(e.message || e));
      }
    })();
    return () => { cancelled = true; if (created) URL.revokeObjectURL(created); };
  }, [src]);

  if (!src) return <div className="text-sm text-slate-400 italic">Aucun fichier attaché</div>;
  if (err) return <div className="text-sm text-red-500">Impossible de charger le PDF ({err})</div>;
  if (!blobUrl) return <div className="text-sm text-slate-400">Chargement…</div>;

  const isImage = /\.(png|jpe?g|webp|gif)$/i.test(src);
  if (isImage) {
    return <img src={blobUrl} alt="pièce" className={`max-w-full ${className}`} />;
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center gap-2 text-xs">
        <Button data-testid="pdf-prev" variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}><ChevronLeft size={13} /></Button>
        <span className="text-slate-500 font-medium">{page} / {numPages || "…"}</span>
        <Button data-testid="pdf-next" variant="outline" size="sm" onClick={() => setPage((p) => Math.min(numPages || p, p + 1))} disabled={page >= numPages}><ChevronRight size={13} /></Button>
        <div className="mx-2 h-4 w-px bg-slate-200" />
        <Button variant="outline" size="sm" onClick={() => setScale((s) => Math.max(0.5, s - 0.2))}><ZoomOut size={13} /></Button>
        <span className="text-slate-500 min-w-[40px] text-center">{Math.round(scale * 100)}%</span>
        <Button variant="outline" size="sm" onClick={() => setScale((s) => Math.min(2.5, s + 0.2))}><ZoomIn size={13} /></Button>
        <div className="flex-1" />
        <a data-testid="pdf-download" href={blobUrl} download className="text-xs text-[#023E73] font-semibold inline-flex items-center gap-1 hover:underline"><Download size={12} /> Télécharger</a>
        <a href={blobUrl} target="_blank" rel="noreferrer" className="text-xs text-[#023E73] font-semibold inline-flex items-center gap-1 hover:underline"><ExternalLink size={12} /> Onglet</a>
      </div>
      <div className="border border-slate-200 rounded-md overflow-auto bg-slate-100" style={{ maxHeight: 600 }}>
        <Document
          file={blobUrl}
          onLoadSuccess={({ numPages: n }) => setNumPages(n)}
          onLoadError={(e) => setErr(String(e?.message || e))}
          loading={<div className="p-6 text-sm text-slate-400">Rendering…</div>}
        >
          <Page pageNumber={page} scale={scale} renderTextLayer={false} renderAnnotationLayer={false} />
        </Document>
      </div>
    </div>
  );
}
